<template>
    <div ref="rootEl" class="pb2-outline">
        <h3 class="pb2-outline__title">{{ t('outline') }}</h3>

        <p v-if="!store.content.value.length" class="pb2-outline__empty">
            {{ t('outline_empty') }}
        </p>

        <TransitionGroup v-else tag="ul" name="pb2-row" class="pb2-outline__tree" role="tree" :aria-label="t('outline')">
            <OutlineNode v-for="(block, index) in store.content.value" :key="nodeKey(block, index)" :block="block" />
        </TransitionGroup>

        <div v-if="store.selectedIds.value.length > 1 && store.mode.value === 'edit'" class="pb2-outline__bulk">
            <span class="pb2-outline__bulk-count">
                {{ t('selected_count', { count: store.selectedIds.value.length }) }}
            </span>
            <button type="button" class="pb2-outline__bulk-action" @click="store.duplicateBlocks(selection())">
                {{ t('duplicate') }}
            </button>
            <button
                type="button"
                class="pb2-outline__bulk-action"
                :disabled="!wrapEligible"
                :title="wrapEligible ? undefined : t('columns_needs_section')"
                @click="store.wrapInSection(selection())"
            >
                {{ t('wrap_in_section') }}
            </button>
            <button
                type="button"
                class="pb2-outline__bulk-action pb2-outline__bulk-action--danger"
                @click="store.removeBlocks(selection())"
            >
                {{ t('delete') }}
            </button>
        </div>

        <div v-if="store.mode.value === 'edit'" class="pb2-outline__add">
            <button type="button" class="pb2-outline__add-toggle" @click="openPalette">
                + {{ t('add_block') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { useBuilderT } from '@src/Common/Builder/host';
import OutlineNode from '@src/Common/Builder/Outline/OutlineNode.vue';
import { OUTLINE_DROP_KEY } from '@src/Common/Builder/Outline/outlineDrop';
import type { OutlineDrop } from '@src/Common/Builder/Outline/outlineDrop';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';
import { useDragMachine } from '@src/Common/Builder/useDragMachine';
import { useSidebarTab } from '@src/Common/Builder/Sidebar/useSidebarTab';
import { usePanelManagerOptional } from '@src/Common/Builder/usePanelManager';
import { canPlace, canWrapInSection, isContainer, slotCountOf } from '@src/Common/Builder/nestingRules';
import { markerId } from '@src/Common/Builder/types';
import type { BlockNode } from '@src/Common/Builder/types';
import type { DropTarget } from '@src/Common/Builder/protocol';

const t = useBuilderT();

const store = useBuilderStore();
const machine = useDragMachine();
const sidebarTab = useSidebarTab();
const panels = usePanelManagerOptional();

/**
 * Reveal the block palette. The full builder surfaces it as a floating panel
 * (panel manager present); the lite layout swaps sidebar tabs instead.
 *
 * @return void No return expected.
 */
const openPalette = (): void => {
    if (panels) {
        panels.open('palette');

        return;
    }

    sidebarTab.value = 'blocks';
};

const rootEl = ref<HTMLElement | null>(null);
const outlineDrop = ref<OutlineDrop | null>(null);
provide(OUTLINE_DROP_KEY, outlineDrop);

const selection = (): string[] => [...store.selectedIds.value];
const wrapEligible = computed(() => canWrapInSection(store.content.value, store.selectedIds.value));

/** Parent/slot/next-sibling info per rendered outline row. */
interface RowInfo {
    parentId: string | null;
    slot: number;
    nextId: string | null;
    block: BlockNode;
}

const buildRowInfo = (): Map<string, RowInfo> => {
    const map = new Map<string, RowInfo>();

    const visit = (blocks: BlockNode[], parentId: string | null, parent: BlockNode | null): void => {
        blocks.forEach((block, index) => {
            const id = markerId(block);

            if (id !== null) {
                const next = blocks[index + 1];
                const rawSlot = Math.max(0, Math.trunc(Number(block.slot ?? 0)));

                map.set(id, {
                    parentId,
                    slot: parent === null ? 0 : Math.min(rawSlot, Math.max(1, slotCountOf(parent)) - 1),
                    nextId: next ? markerId(next) : null,
                    block,
                });
            }

            if (block.children?.length) {
                visit(block.children, id, block);
            }
        });
    };

    visit(store.content.value, null, null);

    return map;
};

/** Zone split: container rows offer an "into" band in the middle half. */
const rowTarget = (
    rowId: string,
    info: Map<string, RowInfo>,
    relative: number,
    dragged: BlockNode[],
): DropTarget | null => {
    const row = info.get(rowId);

    if (!row) {
        return null;
    }

    const intoBand = relative >= 0.25 && relative <= 0.75;

    if (intoBand && isContainer(row.block) && dragged.every((entry) => canPlace(entry, row.block))) {
        outlineDrop.value = { id: rowId, zone: 'into' };

        return { parentId: rowId, slot: 0, beforeId: null };
    }

    const parentBlock = row.parentId === null ? null : (info.get(row.parentId)?.block ?? null);

    if (!dragged.every((entry) => canPlace(entry, parentBlock))) {
        return null;
    }

    const zone = relative < 0.5 ? 'before' : 'after';
    outlineDrop.value = { id: rowId, zone };

    return { parentId: row.parentId, slot: row.slot, beforeId: zone === 'before' ? rowId : row.nextId };
};

const hitTest = (clientX: number, clientY: number, dragged: BlockNode[]): DropTarget | null => {
    const root = rootEl.value;
    const rootRect = root ? root.getBoundingClientRect() : null;
    outlineDrop.value = null;

    if (
        root === null ||
        rootRect === null ||
        clientX < rootRect.left ||
        clientX > rootRect.right ||
        clientY < rootRect.top ||
        clientY > rootRect.bottom
    ) {
        return null;
    }

    const info = buildRowInfo();
    const draggedIds = new Set(dragged.map(markerId).filter((id): id is string => id !== null));
    const inDragged = (id: string): boolean => {
        let current: string | null = id;

        while (current !== null) {
            if (draggedIds.has(current)) {
                return true;
            }

            current = info.get(current)?.parentId ?? null;
        }

        return false;
    };

    for (const row of root.querySelectorAll<HTMLElement>('[data-pb2-block-id]')) {
        const rect = row.getBoundingClientRect();
        const rowId = row.dataset.pb2BlockId;

        if (rowId === undefined || clientY < rect.top || clientY > rect.bottom || inDragged(rowId)) {
            continue;
        }

        return rowTarget(rowId, info, (clientY - rect.top) / Math.max(rect.height, 1), dragged);
    }

    return null;
};

onMounted(() => {
    machine.registerOutlineSurface({
        hitTest,
        clearIndicator: () => {
            outlineDrop.value = null;
        },
    });
});

onBeforeUnmount(() => machine.registerOutlineSurface(null));

/** Stable per-block keys let TransitionGroup FLIP reorders; index fallback
 *  only for anonymous nodes (no id yet — cannot animate identity anyway). */
const nodeKey = (block: BlockNode, index: number): string => markerId(block) ?? `i-${index}`;
</script>

<style scoped>
.pb2-outline {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

/* FLIP reorder (TransitionGroup) — transform-only, no layout thrash. */
.pb2-outline :deep(.pb2-row-move) {
    transition: transform 160ms ease;
}

@media (prefers-reduced-motion: reduce) {
    .pb2-outline :deep(.pb2-row-move) {
        transition: none;
    }
}

.pb2-outline__title {
    margin: 0;
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-outline__empty {
    margin: 0;
    font-size: 0.75rem;
    color: var(--pb2-muted, #a1a1aa);
}

/* Full-bleed rows (VS Code Explorer): cancel the floating-panel body
 * padding so row highlights run edge to edge; row padding restores the
 * content inset. */
.pb2-outline__tree {
    margin: 0 -0.5rem;
    padding: 0;
}

.pb2-outline__add {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding-top: 0.375rem;
    border-top: 1px solid var(--pb2-border, #e4e4e7);
}

.pb2-outline__bulk {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
    background: var(--pb2-surface, #fafafa);
    border: 1px solid var(--pb2-border, #e4e4e7);
    border-radius: var(--pb2-radius, 6px);
}

.pb2-outline__bulk-count {
    font-weight: 600;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-outline__bulk-action {
    padding: 0;
    font-size: 0.75rem;
    text-align: left;
    color: var(--pb2-accent, #2563eb);
    background: none;
    border: 0;
    cursor: pointer;
}

.pb2-outline__bulk-action:disabled {
    opacity: 0.4;
    cursor: default;
}

.pb2-outline__bulk-action--danger {
    color: var(--pb2-danger, #dc2626);
}

.pb2-outline__add-toggle {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    text-align: left;
    color: var(--pb2-accent, #2563eb);
    background: none;
    border: 0;
    cursor: pointer;
}

</style>
